import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';

import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';
import { useFetchCampaigns } from '../../campaigns/hooks/useFetchCampaigns';

import Text from '../../../shared/Text/Text';
import { TopHeader } from '../shared/Styles';
import TinyMceEditor from '../../../shared/TinyMceEditor/TinyMceEditor';
import {
  createPasteProcessor,
  DEFAULT_PASTE_ALLOWED_TAGS,
} from '../../../shared/TinyMceEditor/pasteProcessors';
import { useGetAwsData } from '../../../shared/TileForm/hooks/useGetAwsData';
import {
  IMG_MAX_WIDTH_STYLE,
  createImageUploadHandler,
  isEmailImgSrcAllowed,
  openImagePicker,
} from '../utils/emailImages';
import { RecipientFieldStrip } from './RecipientFieldStrip.jsx';
import { RecipientFieldDialog } from './RecipientFieldDialog.jsx';
import { buildInsertFieldItems } from '../utils/insertFieldItems';
import { buildLinkToAirboItems } from '../utils/linkToAirboItems';
import {
  NONEDITABLE_TOKEN_REGEXP,
  hasPrimaryAirboLink,
} from '../utils/airboLinkTokens';

import { SUBJECT_LIMIT } from '../utils/constants';

export const Email = () => {
  const theme = useTheme();
  const boardEmail = useSelector(state => state.currentBoard.email);
  const boardName = useSelector(state => state.currentBoard.name);
  const { t } = useTranslation('broadcasts');
  const {
    handleChange,
    handleEmailBodyChange,
    values,
    setFieldValue,
    recipientFields,
    userCount,
  } = useBroadcastContext();
  const { campaigns, isFetching } = useFetchCampaigns({ for_broadcast: true });
  // TinyMCE reads its config once at mount, but aws_settings resolves later;
  // the upload handler and paste filter read the latest response via this ref.
  const awsImageData = useGetAwsData({ origin: 'image' });
  const awsImageDataRef = useRef(awsImageData);
  awsImageDataRef.current = awsImageData;
  const [showNewFieldDialog, setShowNewFieldDialog] = useState(false);
  // The live TinyMCE instance, so a field created from the Insert Field menu
  // can have its token dropped at the cursor as soon as it is saved.
  const editorRef = useRef(null);
  const insertToken = token => {
    const editor = editorRef.current;
    if (!editor) return;
    // Runs after the dialog unmounts and releases focus; TinyMCE needs the
    // editor focused for insertContent to land at the cursor.
    setTimeout(() => {
      editor.focus();
      editor.insertContent(`&nbsp;${token}&nbsp;`);
    }, 0);
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  // TinyMCE's `setup` only runs once, so `fetch` closures registered inside
  // it would otherwise capture the initial (stale) recipientFields/openDialog.
  // Keep them in refs and read the refs from `fetch`, which TinyMCE calls
  // fresh every time the menu opens.
  const recipientFieldsRef = useRef(recipientFields);
  useEffect(() => {
    recipientFieldsRef.current = recipientFields;
  }, [recipientFields]);

  const allowRecipientFields = (values.audiences || []).length > 0;
  const allowRecipientFieldsRef = useRef(allowRecipientFields);
  useEffect(() => {
    allowRecipientFieldsRef.current = allowRecipientFields;
  }, [allowRecipientFields]);

  const openDialogRef = useRef(() => setShowNewFieldDialog(true));
  useEffect(() => {
    openDialogRef.current = () => setShowNewFieldDialog(true);
  }, []);

  // Same reason for the Link to Airbo menu: whether a primary link is already
  // written (in either body) and which campaign it points at decide what
  // each menu item does, and both change after `setup` has run.
  const hasPrimaryLink =
    hasPrimaryAirboLink(values.emailBody) ||
    hasPrimaryAirboLink(values.smsBody);
  const airboLinkStateRef = useRef({
    campaignUuid: values.campaignUuid,
    hasPrimaryLink,
  });
  useEffect(() => {
    airboLinkStateRef.current = {
      campaignUuid: values.campaignUuid,
      hasPrimaryLink,
    };
  }, [values.campaignUuid, hasPrimaryLink]);

  return (
    <Container>
      <TopHeader>
        <Text variant="h5">{t('email.header')}</Text>
      </TopHeader>
      <From>
        <TooltipWrapper data-tip={t('email.tooltip')}>
          <Text variant="button">{t('email.from')}</Text>
          <Text variant="body3">
            {t('email.nameEmail', {
              name: boardName,
              email: boardEmail,
            })}
          </Text>
        </TooltipWrapper>
      </From>
      <Subject>
        <Input
          name="subject"
          value={values.subject}
          onChange={handleChange}
          placeholder={t('email.subjectPlaceholder')}
          maxLength={SUBJECT_LIMIT}
        />
      </Subject>
      <EmailBody>
        {!isFetching && (
          <TinyMceEditor
            value={values.emailBody}
            onChange={newValue => handleEmailBodyChange(newValue)}
            config={{
              noneditable_regexp: NONEDITABLE_TOKEN_REGEXP,
              placeholder: t('email.bodyPlaceholder'),
              plugins: ['link', 'lists', 'autolink', 'paste'],
              toolbar:
                'bold italic underline | alignleft aligncenter alignright | bullist numlist | uploadImage | link | linkToAirbo insertField',
              // Pasted/dropped screenshots become blob images and upload
              // straight away through images_upload_handler.
              paste_data_images: true,
              automatic_uploads: true,
              images_upload_handler: createImageUploadHandler({
                getAwsData: () => awsImageDataRef.current,
                t,
              }),
              extended_valid_elements: 'img[src|alt|width|height|style]',
              content_style: `
                body { 
                  color: ${theme.colors.black['100']};
                  font-family: Montserrat, Arial, sans-serif;
                  font-weight: 400;
                  font-size: 14px;
                  -webkit-font-smoothing: antialiased;
                  letter-spacing: 0.1px;
                  margin: 0; 
                }
                p {
                  font-size: 14px;
                  line-height: 22px;
                  letter-spacing: 0.1px;
                  margin: 0 0 16px 0;
                }
                a {
                  color: ${theme.colors.primary[100]};
                  text-decoration: none;
                }
                strong {
                  font-weight: 600;
                }
                ol {
                  list-style-type: decimal;
                  padding: 10px 20px;
                  margin: 0;
                }
                ul {
                  list-style-type: disc;
                  padding: 10px 20px;
                  margin: 0;
                }
                li {
                  line-height: 22px;
                }
                img {
                  max-width: 100%;
                  height: auto;
                }
              `,
              paste_postprocess: createPasteProcessor({
                allowedTags: [...DEFAULT_PASTE_ALLOWED_TAGS, 'img'],
                isImgSrcAllowed: src =>
                  isEmailImgSrcAllowed(src, awsImageDataRef.current?.url),
                imgStyle: IMG_MAX_WIDTH_STYLE,
              }),
              setup: editor => {
                editorRef.current = editor;
                editor.ui.registry.addButton('uploadImage', {
                  icon: 'image',
                  tooltip: t('email.uploadImage'),
                  onAction: () => openImagePicker(editor),
                });
                if (campaigns.length > 0) {
                  editor.ui.registry.addMenuButton('linkToAirbo', {
                    text: t('linkToAirbo'),
                    tooltip: t('linkTooltip'),
                    fetch: callback => {
                      callback(
                        buildLinkToAirboItems({
                          t,
                          campaigns,
                          campaignUuid: airboLinkStateRef.current.campaignUuid,
                          hasPrimaryLink:
                            airboLinkStateRef.current.hasPrimaryLink,
                          setCampaignUuid: uuid =>
                            setFieldValue('campaignUuid', uuid),
                          insert: token =>
                            editor.insertContent(
                              `&nbsp;<span style="color: ${theme.colors.primary[100]}">${token}</span>&nbsp;`,
                            ),
                        }),
                      );
                    },
                  });
                }

                editor.ui.registry.addMenuButton('insertField', {
                  text: t('insertField.button'),
                  tooltip: t('insertField.tooltip'),
                  fetch: callback => {
                    callback(
                      buildInsertFieldItems({
                        t,
                        recipientFields: recipientFieldsRef.current || [],
                        recipientCount: userCount,
                        insert: token =>
                          // Plain text on purpose: a name or code should render in the
                          // surrounding text color, unlike the Airbo link token.
                          editor.insertContent(`&nbsp;${token}&nbsp;`),
                        openDialog: () => openDialogRef.current(),
                        allowRecipientFields: allowRecipientFieldsRef.current,
                      }),
                    );
                  },
                });
              },
            }}
          />
        )}
      </EmailBody>
      <RecipientFieldStrip body={values.emailBody} />
      {showNewFieldDialog && (
        <RecipientFieldDialog
          isOpen={showNewFieldDialog}
          onClose={() => setShowNewFieldDialog(false)}
          onCreated={field => insertToken(field.token)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  min-height: 500px;
`;

const TooltipWrapper = styled.div`
  display: inline-block;
`;

const From = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${themeGet('colors.black.10')};
  max-height: 58px;
`;
const Subject = styled(From)``;

const EmailBody = styled.div`
  position: relative;
  .tox-tinymce {
    border: none;

    .tox-editor-header {
      box-shadow: none !important;
    }

    .tox-edit-area::before {
      border: none;
    }

    .tox-edit-area {
      margin: 20px;
    }

    .tox-toolbar__primary {
      background: none;
      border-bottom: solid 1px;
      border-color: ${themeGet('colors.black.10')};

      .tox-toolbar__group {
        border-color: ${themeGet('colors.black.10')} !important;
      }
    }

    .tox-statusbar {
      display: none;
    }
  }
`;
const Input = styled.input`
  width: 100%;
  border: 0;
  padding: 0;
  outline: 0;
  font-weight: 500;
  font-size: 14px;
  color: ${themeGet('colors.black.80')};
  line-height: 18px;
  font-family: 'Montserrat', sans-serif;
`;
